import { innovorderApi } from '../index';
import { ApiResponse } from '../request.types';
import {
    AddPickupPointPayload,
    AutocompleteSuggestionAddress,
    DeletePickupPointPayload,
    FormattedAddress,
    GetPickupPointsByBrandPayload,
    GetPickupPointsPayload,
    PickupPoint,
    PutPickupPointPayload,
} from './pickupPoint.type';

const pickupPointApi = innovorderApi.injectEndpoints({
    endpoints: (build) => ({
        getPickupPointsByRestaurant: build.query<PickupPoint[], GetPickupPointsPayload>({
            query: ({ restaurantId, token }) => ({
                url: `/restaurants/${restaurantId}/pickup_points`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<PickupPoint[]>) => {
                if (!response || !response.data) {
                    throw new Error('Invalid response structure');
                }
                return response.data;
            },
            extraOptions: { maxRetries: 3 },
        }),
        getPickupPointsByBrand: build.query<PickupPoint[], GetPickupPointsByBrandPayload>({
            query: ({ brandId }) => ({
                url: `/brands/${brandId}/pickup_points`,
                method: 'GET',
            }),
            transformResponse: (response: ApiResponse<PickupPoint[]>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        addPickupPoint: build.mutation<PickupPoint[], AddPickupPointPayload>({
            query: ({ restaurantId, name, address, token }) => ({
                url: `/restaurants/${restaurantId}/pickup_points`,
                method: 'POST',
                body: { name, address },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<PickupPoint[]>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        putPickupPoint: build.mutation<PickupPoint[], PutPickupPointPayload>({
            query: ({ pickupPointId, name, address, token }) => ({
                url: `/pickup_points/${pickupPointId}`,
                method: 'PUT',
                body: { name, address },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<PickupPoint[]>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        deletePickupPoint: build.mutation<PickupPoint[], DeletePickupPointPayload>({
            query: ({ pickupPointId, token }) => ({
                url: `/pickup_points/${pickupPointId}`,
                method: 'DELETE',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<PickupPoint[]>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),

        getLocationAutocomplete: build.query<
            { suggestions: AutocompleteSuggestionAddress[] },
            { searchText: string; sessiontoken: string; token: string }
        >({
            query: ({ searchText, sessiontoken, token }) => ({
                url: `/location/autocomplete`,
                method: 'GET',
                params: { searchText, sessiontoken },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<{ suggestions: AutocompleteSuggestionAddress[] }>) =>
                response.data,
            extraOptions: { maxRetries: 3 },
        }),
        getLocationPlaceDetails: build.query<
            { place: FormattedAddress },
            { placeId: string; sessiontoken: string; token: string }
        >({
            query: ({ placeId, sessiontoken, token }) => ({
                url: `/location/place_details`,
                method: 'GET',
                params: { placeId, sessiontoken },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<{ place: FormattedAddress }>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
    }),
});

export const {
    useGetPickupPointsByRestaurantQuery,
    useGetPickupPointsByBrandQuery,
    useAddPickupPointMutation,
    usePutPickupPointMutation,
    useDeletePickupPointMutation,
    useGetLocationAutocompleteQuery,
    useGetLocationPlaceDetailsQuery,
} = pickupPointApi;
